/**
 * Ragdolls: a person falling as a body does, rather than as code poses them.
 *
 * The rig's joints become eighteen points (`J`), each with a share of an
 * 80 kg body's weight, stepped by Verlet integration at a fixed 240 Hz: a
 * point's velocity is where it is less where it was. After each step the
 * points are pushed back into a body (position-based dynamics):
 *
 * - **Bones** keep their lengths: every bone of the rig, the chest and
 *   shoulders as one rigid piece, and the hips as another.
 * - **Joints** keep within what a body can do. The spine, the neck, the
 *   hips and the ankles bend only so far, as ranges on the distances across
 *   them, measured once from the standing figure turned through each joint's
 *   limits. An elbow and a knee straighten but go no further, and a knee
 *   folds only behind it: one found folding forward is brought back straight,
 *   from where it bends the right way again. An upper arm goes no further
 *   back than 57°, the toes follow the shin, and arms and legs are kept out
 *   of the chest and the belly. A limit is the body pushing on itself, so
 *   both sides of a joint give way, each by its weight.
 * - **The ground** holds each point up by its own radius (a head is a head
 *   wide), and drags at it by Coulomb friction: the more a point is pushed
 *   out of the ground, the harder it is held. A body landing flat loses
 *   speed on the blow, then slides at μg until it stops.
 * - **Walls**, posts and cars push the trunk and the head out, sideways.
 *
 * It comes to rest when no point has moved 3 cm in half a second, and then
 * sleeps. Nothing here knows about three.js: the crowd draws
 * a person from the points (`CharacterCrowd.placeRagdoll`), and the game
 * starts one from the joints of the pose that was being drawn.
 */

import { BELLY, BODY, CHEST, HEAD_CENTRE, HIP_DROP, PALM, TOE } from './body.js';
import type { FallStart } from './fall.js';

export type { FallKind, FallStart } from './fall.js';
export { FALL_KINDS } from './fall.js';

/**
 * The points, in the order the crowd reads and writes them. Left is the
 * person's own left. `belly` is the front of the pelvis: with the hip joints
 * and the waist it makes the hips a solid, not a sliver.
 */
export const J = {
  waist: 0, neck: 1, head: 2, chest: 3,
  shoulderL: 4, elbowL: 5, wristL: 6,
  shoulderR: 7, elbowR: 8, wristR: 9,
  hipL: 10, kneeL: 11, ankleL: 12, toeL: 13,
  hipR: 14, kneeR: 15, ankleR: 16, toeR: 17,
  belly: 18,
} as const;
export const POINTS = 19;

/** Each side's limb points, left then right. */
export const SIDES = [
  { shoulder: J.shoulderL, elbow: J.elbowL, wrist: J.wristL, hip: J.hipL, knee: J.kneeL, ankle: J.ankleL, toe: J.toeL, out: -1 },
  { shoulder: J.shoulderR, elbow: J.elbowR, wrist: J.wristR, hip: J.hipR, knee: J.kneeR, ankle: J.ankleR, toe: J.toeR, out: 1 },
] as const;

/** Each point's share of the body, kg: about 80 with the riding kit. */
const MASS = [5, 5, 6, 10, 5, 2, 1.2, 5, 2, 1.2, 8, 5, 2.5, 0.8, 8, 5, 2.5, 0.8, 5];
/** How far the body's surface is out from each point, metres: what keeps it off the ground. */
export const RADIUS = [0.1, 0.07, 0.12, 0.04, 0.08, 0.05, 0.045, 0.08, 0.05, 0.045, 0.09, 0.06, 0.06, 0.035, 0.09, 0.06, 0.06, 0.035, 0.04];
/** The points kept out of walls, and how wide the body is round each. */
const WALL: ReadonlyArray<readonly [number, number]> = [[J.head, 0.13], [J.chest, 0.12], [J.waist, 0.15], [J.hipL, 0.1], [J.hipR, 0.1]];

/** The step, seconds: short enough that a body at motorway speed moves a few centimetres. */
export const STEP = 1 / 240;
/** Passes of the constraints per step. */
const ITERATIONS = 6;
const GRAVITY = 9.81;
/**
 * Friction of a person on the ground: sliding, and the grip that holds one
 * still. Riding kit on tarmac slides at about 0.5–0.7, as does a body on grass.
 */
export const MU = 0.6;
const MU_STATIC = 0.8;
/** Air drag, per metre: ½ρ·CdA over the mass, for a tumbling body (CdA about 0.6 m²). */
const DRAG = (0.5 * 1.2 * 0.6) / 80;
/** No point further than this from where it was, metres, for `REST_TIME` seconds: at rest. */
const REST_DRIFT = 0.03;
const REST_TIME = 0.5;
/** However it lies, after this long it is at rest: a body on a slope does not slide for ever. */
const GIVE_UP = 12;

/** The standing figure's points, metres, feet on y = 0 facing -Z. */
export function standing(): Float32Array {
  const p = new Float32Array(POINTS * 3);
  const set = (i: number, x: number, y: number, z: number) => { p[i * 3] = x; p[i * 3 + 1] = y; p[i * 3 + 2] = z; };
  const waist = BODY.hip + BODY.waist, neck = waist + BODY.torsoHeight, hip = BODY.hip - HIP_DROP;
  set(J.waist, 0, waist, 0);
  set(J.neck, 0, neck, 0);
  set(J.head, 0, neck + HEAD_CENTRE, 0);
  set(J.chest, CHEST[0], waist + CHEST[1], CHEST[2]);
  set(J.belly, BELLY[0], BODY.hip + BELLY[1], BELLY[2]);
  for (const s of SIDES) {
    const sx = s.out * BODY.shoulderWidth, sy = waist + BODY.shoulderHeight;
    set(s.shoulder, sx, sy, 0);
    set(s.elbow, sx, sy - BODY.upperArm, 0);
    set(s.wrist, sx, sy - BODY.upperArm - PALM, 0);
    const hx = s.out * BODY.hipWidth;
    set(s.hip, hx, hip, 0);
    set(s.knee, hx, hip - BODY.thigh, 0);
    set(s.ankle, hx, hip - BODY.thigh - BODY.shin, 0);
    set(s.toe, hx + TOE[0], hip - BODY.thigh - BODY.shin + TOE[1], TOE[2]);
  }
  return p;
}

// --- the constraints, built once ----------------------------------------------

/** Distance constraints: the two points, and the least and most they may be apart. */
const CA: number[] = [], CB: number[] = [], CMIN: number[] = [], CMAX: number[] = [];

const REST = standing();
const at = (p: ArrayLike<number>, i: number): [number, number, number] => [p[i * 3], p[i * 3 + 1], p[i * 3 + 2]];
const dist = (a: readonly number[], b: readonly number[]) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

function rigid(a: number, b: number): void {
  const d = dist(at(REST, a), at(REST, b));
  CA.push(a); CB.push(b); CMIN.push(d); CMAX.push(d);
}

/** Turn `p` about `pivot` by `x` (nodding, negative forward), then `z` (to the side), then `y` (twist). */
function turn(p: readonly number[], pivot: readonly number[], x: number, y: number, z: number): [number, number, number] {
  let px = p[0] - pivot[0], py = p[1] - pivot[1], pz = p[2] - pivot[2];
  [py, pz] = [py * Math.cos(x) - pz * Math.sin(x), py * Math.sin(x) + pz * Math.cos(x)];
  [px, py] = [px * Math.cos(z) - py * Math.sin(z), px * Math.sin(z) + py * Math.cos(z)];
  [px, pz] = [px * Math.cos(y) + pz * Math.sin(y), -px * Math.sin(y) + pz * Math.cos(y)];
  return [px + pivot[0], py + pivot[1], pz + pivot[2]];
}

/**
 * A ball joint's limits, as ranges on the distances between what it turns
 * (`moving`) and what it is fixed to (`fixed`): turned through every
 * combination of the limits, the nearest and furthest each pair comes.
 */
function joint(pivot: number, moving: readonly number[], fixed: readonly number[],
  x: readonly [number, number], z: readonly [number, number], y: readonly [number, number]): void {
  const o = at(REST, pivot);
  for (const m of moving) {
    for (const f of fixed) {
      let lo = Infinity, hi = 0;
      for (let i = 0; i <= 8; i++) for (let j = 0; j <= 6; j++) for (let k = 0; k <= 6; k++) {
        const q = turn(at(REST, m), o, x[0] + (x[1] - x[0]) * i / 8, y[0] + (y[1] - y[0]) * k / 6, z[0] + (z[1] - z[0]) * j / 6);
        const d = dist(q, at(REST, f));
        lo = Math.min(lo, d); hi = Math.max(hi, d);
      }
      CA.push(m); CB.push(f); CMIN.push(lo * 0.99); CMAX.push(hi * 1.01);
    }
  }
}

/** A hinge: how near and far the ends of two bones of `a` and `b` may be, from straight to bent by `most`. */
function hinge(from: number, to: number, a: number, b: number, most: number): void {
  CA.push(from); CB.push(to);
  CMIN.push(Math.sqrt(a * a + b * b + 2 * a * b * Math.cos(most)));
  CMAX.push(a + b);
}

{
  // The chest and shoulders, one piece with the waist; the hips another.
  const chest = [J.waist, J.neck, J.chest, J.shoulderL, J.shoulderR];
  for (let i = 0; i < chest.length; i++) for (let k = i + 1; k < chest.length; k++) rigid(chest[i], chest[k]);
  const pelvis = [J.hipL, J.hipR, J.waist, J.belly];
  for (let i = 0; i < pelvis.length; i++) for (let k = i + 1; k < pelvis.length; k++) rigid(pelvis[i], pelvis[k]);
  // The spine: bending 50° forward, 20° back, 30° to each side, twisting 35°.
  joint(J.waist, [J.shoulderL, J.shoulderR, J.chest], [J.hipL, J.hipR, J.belly], [-0.87, 0.35], [-0.52, 0.52], [-0.61, 0.61]);
  // The neck: chin to chest, 35° back, 35° to each side.
  rigid(J.neck, J.head);
  joint(J.neck, [J.head], [J.chest, J.shoulderL, J.shoulderR], [-0.9, 0.6], [-0.6, 0.6], [0, 0]);
  for (const s of SIDES) {
    rigid(s.shoulder, s.elbow); rigid(s.elbow, s.wrist);
    hinge(s.shoulder, s.wrist, BODY.upperArm, PALM, 2.5);
    rigid(s.hip, s.knee); rigid(s.knee, s.ankle); rigid(s.ankle, s.toe);
    hinge(s.hip, s.ankle, BODY.thigh, BODY.shin, 2.6);
    // The hip: the thigh 125° forward, 20° back, 60° out and 20° across.
    const other = s.out < 0 ? J.hipR : J.hipL;
    joint(s.hip, [s.knee], [J.belly, J.waist, other], [-0.35, 2.2], s.out < 0 ? [-1.05, 0.35] : [-0.35, 1.05], [0, 0]);
    // The ankle: pointing 45°, flexing 20°.
    joint(s.ankle, [s.toe], [s.knee], [-0.79, 0.35], [-0.2, 0.2], [0, 0]);
  }
}

const COUNT = CA.length;
const W = MASS.map(m => 1 / m);
/**
 * How readily each point gives way to the body's own pull this step: a
 * point on the ground ten times less. Pulled down into the ground by a
 * joint and pushed back out by it every step, a point resting on it would
 * gain speed from nowhere and throw the body about; held, the joint moves
 * what is free to move instead, as a body lying down moves its limbs and
 * not the hip it lies on.
 */
const IW = Float64Array.from(W);
/** How much less a point on the ground gives way. */
const GROUNDED = 0.1;

// --- a ragdoll ------------------------------------------------------------------

export interface Ragdoll {
  /** Where each point is, x y z, metres. */
  p: Float32Array;
  /** Where each was a step ago: the velocity is the difference over `STEP`. */
  q: Float32Array;
  /** The frame's time not yet stepped, seconds. */
  carry: number;
  /** How long nothing has moved, seconds, since `mark`, where the points were; and how long since it started. */
  still: number;
  mark: Float32Array;
  age: number;
  resting: boolean;
  /** The ground under each point at the start of the frame, where it was sampled, and its slope there. */
  ground: Float32Array;
  sampledAt: Float32Array;
  slope: [number, number];
  /** How hard each point was pushed out of the ground this step, metres: what the friction holds by. */
  pressed: Float32Array;
}

/** A ragdoll from a pose: the joints (as `J`, x y z), standing still. */
export function ragdollFrom(joints: ArrayLike<number>): Ragdoll {
  const p = Float32Array.from(joints);
  return {
    p, q: Float32Array.from(p), carry: 0, still: 0, mark: Float32Array.from(p), age: 0, resting: false,
    ground: new Float32Array(POINTS), sampledAt: new Float32Array(POINTS * 2), slope: [0, 0], pressed: new Float32Array(POINTS),
  };
}

/** Set point `i` moving at (vx, vy, vz) m/s. */
export function setVelocity(r: Ragdoll, i: number, vx: number, vy: number, vz: number): void {
  r.q[i * 3] = r.p[i * 3] - vx * STEP;
  r.q[i * 3 + 1] = r.p[i * 3 + 1] - vy * STEP;
  r.q[i * 3 + 2] = r.p[i * 3 + 2] - vz * STEP;
}

/** Between the hip joints: where the body is. Into `out`, which is made if not given. */
export function hips(p: ArrayLike<number>, out: [number, number, number] = [0, 0, 0]): [number, number, number] {
  out[0] = (p[J.hipL * 3] + p[J.hipR * 3]) / 2;
  out[1] = (p[J.hipL * 3 + 1] + p[J.hipR * 3 + 1]) / 2;
  out[2] = (p[J.hipL * 3 + 2] + p[J.hipR * 3 + 2]) / 2;
  return out;
}

/**
 * How a rider leaves the bike in a head-on crash. The bike stops; the rider
 * goes on, over the bars. The knees and thighs strike the tank and the bars
 * and take a little of the speed (`keep`); the hips, caught there, are
 * thrown up (`lift`, a share of the speed), and the body pitches forward
 * over them (`pitch`, rad/s per m/s) and turns a somersault in the air.
 */
export const THROW = { keep: 0.9, lift: 0.2, liftMost: 3.5, pitch: 0.3, pitchMost: 5 } as const;

/**
 * Throw a ragdoll over the bars: going at `v` (x y z, m/s) along `forward`
 * (a unit x, z on the ground) when the bike stopped. With `backward`, off
 * the back instead, as a bike landing on its back wheel throws its rider:
 * tipping over backwards, with less lift.
 */
export function throwOverBars(r: Ragdoll, v: { x: number; y: number; z: number }, forward: { x: number; z: number }, backward = false): void {
  const speed = Math.hypot(v.x, v.z);
  const lift = Math.min(THROW.liftMost, THROW.lift * speed) * (backward ? 0.4 : 1);
  const rate = Math.min(THROW.pitchMost, THROW.pitch * speed) * (backward ? -0.6 : 1);
  // Pitching forward is a turn about the rider's left: up goes to forward.
  const wx = forward.z * rate, wz = -forward.x * rate;
  const [cx, cy, cz] = hips(r.p);
  for (let i = 0; i < POINTS; i++) {
    const rx = r.p[i * 3] - cx, ry = r.p[i * 3 + 1] - cy, rz = r.p[i * 3 + 2] - cz;
    // ω × r, with ω = (wx, 0, wz).
    setVelocity(r, i, THROW.keep * v.x - wz * ry, THROW.keep * v.y + lift + wz * rx - wx * rz, THROW.keep * v.z + wx * ry);
  }
}

/** How fast the body as a whole is going, m/s: its points' velocities, weighed by their shares of it. */
export function bodyVelocity(r: Ragdoll): [number, number, number] {
  let x = 0, y = 0, z = 0, mass = 0;
  for (let i = 0; i < POINTS; i++) {
    x += (r.p[i * 3] - r.q[i * 3]) * MASS[i]; y += (r.p[i * 3 + 1] - r.q[i * 3 + 1]) * MASS[i]; z += (r.p[i * 3 + 2] - r.q[i * 3 + 2]) * MASS[i];
    mass += MASS[i];
  }
  return [x / mass / STEP, y / mass / STEP, z / mass / STEP];
}

/** Set the whole body moving at (vx, vy, vz) m/s: falling, as it was. */
export function launch(r: Ragdoll, vx: number, vy: number, vz: number): void {
  for (let i = 0; i < POINTS; i++) setVelocity(r, i, vx, vy, vz);
}

/**
 * How a person struck by a vehicle goes. The bumper takes the legs from
 * under them and the bonnet's edge the hips, so the body below `bumper`
 * metres over its feet is sent on a little faster than the hit's share
 * (`dv`, scene x, z, m/s), the head and shoulders a little slower: it tips
 * back toward the vehicle as it is lifted, wrapping onto the bonnet, and is
 * thrown on at about the vehicle's speed.
 */
export function struckBy(r: Ragdoll, dv: { x: number; z: number }, bumper: number): void {
  const p = r.p;
  let feet = Infinity, head = -Infinity;
  for (let i = 0; i < POINTS; i++) { feet = Math.min(feet, p[i * 3 + 1]); head = Math.max(head, p[i * 3 + 1]); }
  const speed = Math.hypot(dv.x, dv.z), lift = Math.min(STRUCK.liftMost, STRUCK.lift * speed);
  for (let i = 0; i < POINTS; i++) {
    const h = p[i * 3 + 1] - feet;
    const k = h <= bumper ? STRUCK.legs : STRUCK.legs + (STRUCK.head - STRUCK.legs) * Math.min(1, (h - bumper) / Math.max(0.1, head - feet - bumper));
    setVelocity(r, i, dv.x * k, lift * Math.min(1, h / Math.max(0.1, bumper)), dv.z * k);
  }
}

/** How much of a hit's share the legs and the head are sent on with, and the lift: a share of the speed, up to so much, m/s. */
export const STRUCK = { legs: 1.12, head: 0.85, lift: 0.18, liftMost: 3 } as const;

/** Set a body going as `fall` says. */
export function startFall(body: Ragdoll, fall: FallStart): void {
  const [x, y, z] = fall.v;
  if (fall.kind === 'bars' || fall.kind === 'back') {
    const f = fall.forward ?? [0, -1];
    throwOverBars(body, { x, y, z }, { x: f[0], z: f[1] }, fall.kind === 'back');
  } else if (fall.kind === 'struck') {
    struckBy(body, { x, z }, fall.bumper ?? 0.5);
  } else {
    fellFrom(body, x, y, z);
  }
}

/**
 * Landing too hard to stand, how far the body gives: the most it sinks, the
 * most it goes on along the ground, m/s, and how hard it tips over, m/s at
 * the head.
 */
export const FELL = { sink: 6, along: 1.5, tip: 1.4 } as const;

/**
 * A body landing from a height (`vx`, `vy`, `vz` how it came down, m/s): it
 * does not bounce or slide off, however fast it fell. The feet stop on the
 * ground; the legs buckle under it, the body above sinking the faster the
 * higher it is, and it topples on the way it was going (or, coming straight
 * down, forward over its front). What it fell at is soaked up by the
 * crumpling: from a roof or out of the sky, it ends in a heap where it hit.
 */
export function fellFrom(r: Ragdoll, vx: number, vy: number, vz: number): void {
  const p = r.p;
  let feet = Infinity, head = -Infinity;
  for (let i = 0; i < POINTS; i++) { feet = Math.min(feet, p[i * 3 + 1]); head = Math.max(head, p[i * 3 + 1]); }
  const span = Math.max(0.2, head - feet);
  const sink = Math.max(-FELL.sink, Math.min(0, vy));
  const ground = Math.hypot(vx, vz), keep = ground > FELL.along ? FELL.along / ground : 1;
  // Which way it goes over: the way it was going, else the way its front faces.
  let tx = vx, tz = vz;
  if (ground < 0.3) {
    tx = p[J.belly * 3] - p[J.waist * 3];
    tz = p[J.belly * 3 + 2] - p[J.waist * 3 + 2];
  }
  const tl = Math.hypot(tx, tz) || 1;
  for (let i = 0; i < POINTS; i++) {
    const up = Math.max(0, Math.min(1, (p[i * 3 + 1] - feet) / span));
    setVelocity(r, i, vx * keep * up + (tx / tl) * FELL.tip * up, sink * up, vz * keep * up + (tz / tl) * FELL.tip * up);
  }
}

/** The ground's height under scene (x, z) near height `y`, or undefined while it is not there. */
export type RagdollGround = (x: number, z: number, y: number) => number | undefined;
/** Push a round body of `radius` at (x, y, z) out of whatever is solid there: where it may be, or undefined when clear. */
export type RagdollCollide = (x: number, y: number, z: number, radius: number) => { x: number; z: number } | undefined;

/**
 * Step the ragdoll on by `dt` seconds. The ground is sampled once a frame
 * under each point and followed along its slope between, so a frame costs
 * twenty-two samples whatever the step; walls are tried once a frame too.
 */
export function stepRagdoll(r: Ragdoll, dt: number, ground: RagdollGround, collide?: RagdollCollide): void {
  if (r.resting) return;
  sampleGround(r, ground);
  r.carry += dt;
  while (r.carry >= STEP) {
    r.carry -= STEP;
    r.age += STEP;
    integrate(r);
    r.pressed.fill(0);
    grounded(r);
    for (let k = 0; k < ITERATIONS; k++) {
      solve(r.p);
      onGround(r);
    }
    friction(r);
    settle(r);
    turnFeet(r);
    if (drifted(r) > REST_DRIFT) {
      r.mark.set(r.p);
      r.still = 0;
    } else {
      r.still += STEP;
    }
    if (r.still >= REST_TIME || r.age >= GIVE_UP) {
      r.resting = true;
      r.q.set(r.p);
      return;
    }
  }
  if (collide !== undefined) walls(r, collide);
}

/** Weigh down the points on the ground, or within a centimetre of it: see `IW`. */
function grounded(r: Ragdoll): void {
  for (let i = 0; i < POINTS; i++) {
    IW[i] = r.p[i * 3 + 1] <= groundAt(r, i) + RADIUS[i] + 0.01 ? W[i] * GROUNDED : W[i];
  }
}

/** Higher than this over the ground, metres, a point is in the air: see `sampleGround`. */
const AIRBORNE = 0.8;

function sampleGround(r: Ragdoll, ground: RagdollGround): void {
  const p = r.p;
  for (let i = 0; i < POINTS; i++) {
    const x = p[i * 3], y = p[i * 3 + 1], z = p[i * 3 + 2];
    // A point well up in the air, not far from where the ground was last
    // found under it, follows that ground's slope: half the samples saved
    // in flight, and none of them matter until it comes down.
    if (r.age > 0 && y > groundAt(r, i) + AIRBORNE && Math.hypot(x - r.sampledAt[i * 2], z - r.sampledAt[i * 2 + 1]) < 3) continue;
    const g = ground(x, z, y);
    // Not streamed in: keep what was there, followed along its slope.
    if (g !== undefined) {
      r.ground[i] = g;
      r.sampledAt[i * 2] = x;
      r.sampledAt[i * 2 + 1] = z;
    } else if (r.age === 0) {
      r.ground[i] = -Infinity;
    }
  }
  const [cx, cy, cz] = hips(p);
  const e = ground(cx + 0.5, cz, cy), w = ground(cx - 0.5, cz, cy);
  const s = ground(cx, cz + 0.5, cy), n = ground(cx, cz - 0.5, cy);
  r.slope[0] = e !== undefined && w !== undefined ? Math.max(-2, Math.min(2, e - w)) : 0;
  r.slope[1] = s !== undefined && n !== undefined ? Math.max(-2, Math.min(2, s - n)) : 0;
}

function integrate(r: Ragdoll): void {
  const { p, q } = r;
  for (let i = 0; i < POINTS * 3; i += 3) {
    const vx = p[i] - q[i], vy = p[i + 1] - q[i + 1], vz = p[i + 2] - q[i + 2];
    // Slowing by DRAG·v² over the step: DRAG·(|d|/STEP)·STEP of it, as d is the step's distance.
    const drag = 1 - DRAG * Math.hypot(vx, vy, vz);
    q[i] = p[i]; q[i + 1] = p[i + 1]; q[i + 2] = p[i + 2];
    p[i] += vx * drag;
    p[i + 1] += vy * drag - GRAVITY * STEP * STEP;
    p[i + 2] += vz * drag;
  }
}

/** The ground's height under point `i` where it is now, from the frame's sample and the slope. */
function groundAt(r: Ragdoll, i: number): number {
  const p = r.p;
  return r.ground[i] + r.slope[0] * (p[i * 3] - r.sampledAt[i * 2]) + r.slope[1] * (p[i * 3 + 2] - r.sampledAt[i * 2 + 1]);
}

function onGround(r: Ragdoll): void {
  const p = r.p;
  // The ground's up, from its slope: one plane under the whole body.
  const gx = -r.slope[0], gz = -r.slope[1], len = Math.hypot(gx, 1, gz);
  const nx = gx / len, ny = 1 / len, nz = gz / len;
  for (let i = 0; i < POINTS; i++) {
    const under = groundAt(r, i) + RADIUS[i] - p[i * 3 + 1];
    if (!(under > 0)) continue;
    // Out along the ground's normal, as far as it takes to clear it.
    const out = under * ny;
    p[i * 3] += nx * out;
    p[i * 3 + 1] += ny * out;
    p[i * 3 + 2] += nz * out;
    // The hardest push of the step, not their sum: a joint and the ground
    // taking turns at a point would otherwise hold it as if it bore the
    // weight of several bodies, and a body tumbling along stopped in half the
    // distance a real one slides.
    r.pressed[i] = Math.max(r.pressed[i], out);
  }
}

/**
 * Coulomb friction on each point the ground pushed out this step: what it
 * slid along the ground is taken back, all of it if it was held less than
 * the grip, else by μ times how hard it was pressed.
 */
function friction(r: Ragdoll): void {
  const { p, q } = r;
  const gx = -r.slope[0], gz = -r.slope[1], len = Math.hypot(gx, 1, gz);
  const nx = gx / len, ny = 1 / len, nz = gz / len;
  for (let i = 0; i < POINTS; i++) {
    const pressed = r.pressed[i];
    if (pressed <= 0) continue;
    const o = i * 3;
    let dx = p[o] - q[o], dy = p[o + 1] - q[o + 1], dz = p[o + 2] - q[o + 2];
    const dn = dx * nx + dy * ny + dz * nz;
    dx -= nx * dn; dy -= ny * dn; dz -= nz * dn;
    const slid = Math.hypot(dx, dy, dz);
    if (slid === 0) continue;
    const k = slid <= MU_STATIC * pressed ? 1 : (MU * pressed) / slid;
    p[o] -= dx * k; p[o + 1] -= dy * k; p[o + 2] -= dz * k;
  }
}

/** The trunk's points: on the ground, the body is lying down. */
const TRUNK: readonly number[] = [J.waist, J.chest, J.neck, J.shoulderL, J.shoulderR, J.hipL, J.hipR, J.belly];
/** How fast a body lying down stops moving its limbs, per second: the joints' own friction, and a little will. */
const LYING_DAMPING = 3;

/**
 * Lying down, the limbs settle against the body: without the joints' own
 * friction a limb hung over something would swing for ever. Only movement
 * against the body as a whole is slowed, so a body sliding along the road is
 * left to the road's friction; in the air nothing slows it but the air, so
 * a tumble keeps its spin.
 */
function settle(r: Ragdoll): void {
  let down = 0;
  for (const i of TRUNK) if (IW[i] !== W[i]) down++;
  if (down < 3) return;
  const { p, q } = r;
  let mx = 0, my = 0, mz = 0, mass = 0;
  for (let i = 0; i < POINTS; i++) {
    mx += (p[i * 3] - q[i * 3]) * MASS[i]; my += (p[i * 3 + 1] - q[i * 3 + 1]) * MASS[i]; mz += (p[i * 3 + 2] - q[i * 3 + 2]) * MASS[i];
    mass += MASS[i];
  }
  mx /= mass; my /= mass; mz /= mass;
  const k = 1 - LYING_DAMPING * STEP;
  for (let i = 0; i < POINTS; i++) {
    const o = i * 3;
    q[o] = p[o] - (mx + (p[o] - q[o] - mx) * k);
    q[o + 1] = p[o + 1] - (my + (p[o + 1] - q[o + 1] - my) * k);
    q[o + 2] = p[o + 2] - (mz + (p[o + 2] - q[o + 2] - mz) * k);
  }
}

/**
 * The furthest any point is from where it was when the body last moved,
 * metres. Judged by where the points are rather than how fast they are
 * going: a point held between two limits that do not quite agree is pushed
 * a little each way every step, which reads as speed, but goes nowhere.
 */
function drifted(r: Ragdoll): number {
  let most = 0;
  for (let i = 0; i < POINTS * 3; i += 3) {
    most = Math.max(most, (r.p[i] - r.mark[i]) ** 2 + (r.p[i + 1] - r.mark[i + 1]) ** 2 + (r.p[i + 2] - r.mark[i + 2]) ** 2);
  }
  return Math.sqrt(most);
}

/** Push the trunk and the head out of walls, and take away the speed into them, with a little friction along. */
function walls(r: Ragdoll, collide: RagdollCollide): void {
  const { p, q } = r;
  for (const [i, radius] of WALL) {
    const o = i * 3;
    const out = collide(p[o], p[o + 1], p[o + 2], radius);
    if (out === undefined) continue;
    let nx = out.x - p[o], nz = out.z - p[o + 2];
    const push = Math.hypot(nx, nz);
    if (push === 0) continue;
    nx /= push; nz /= push;
    // The whole body moves out with it, so the wall does not pull it apart.
    for (let k = 0; k < POINTS; k++) {
      p[k * 3] += nx * push; p[k * 3 + 2] += nz * push;
      q[k * 3] += nx * push; q[k * 3 + 2] += nz * push;
    }
    for (let k = 0; k < POINTS; k++) {
      const c = k * 3;
      const vx = p[c] - q[c], vz = p[c + 2] - q[c + 2];
      const into = vx * nx + vz * nz;
      if (into >= 0) continue;
      const tx = vx - nx * into, tz = vz - nz * into, along = Math.hypot(tx, tz);
      const keep = along > 0 ? Math.max(0, 1 - (MU * -into) / along) : 0;
      q[c] = p[c] - tx * keep;
      q[c + 2] = p[c + 2] - tz * keep;
    }
  }
}

// --- one pass of the constraints ------------------------------------------------

/** One pass over every bone and every joint's limit, in place on the points `p`. */
function solve(p: Float32Array): void {
  for (let c = 0; c < COUNT; c++) {
    const a = CA[c] * 3, b = CB[c] * 3;
    const dx = p[b] - p[a], dy = p[b + 1] - p[a + 1], dz = p[b + 2] - p[a + 2];
    const d = Math.hypot(dx, dy, dz);
    if (d < 1e-9) continue;
    const want = d < CMIN[c] ? CMIN[c] : d > CMAX[c] ? CMAX[c] : d;
    if (want === d) continue;
    const wa = IW[CA[c]], wb = IW[CB[c]];
    const k = (d - want) / d / (wa + wb);
    p[a] += dx * k * wa; p[a + 1] += dy * k * wa; p[a + 2] += dz * k * wa;
    p[b] -= dx * k * wb; p[b + 1] -= dy * k * wb; p[b + 2] -= dz * k * wb;
  }
  limits(p);
}

const v3 = (): [number, number, number] => [0, 0, 0];
const R = v3(), U = v3(), F = v3(), TR = v3(), TU = v3(), TF = v3();

function sub(out: number[], p: ArrayLike<number>, a: number, b: number): number[] {
  out[0] = p[a * 3] - p[b * 3]; out[1] = p[a * 3 + 1] - p[b * 3 + 1]; out[2] = p[a * 3 + 2] - p[b * 3 + 2];
  return out;
}
function unit(v: number[]): number[] {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  v[0] /= l; v[1] /= l; v[2] /= l;
  return v;
}
function cross(out: number[], a: readonly number[], b: readonly number[]): number[] {
  const x = a[1] * b[2] - a[2] * b[1], y = a[2] * b[0] - a[0] * b[2], z = a[0] * b[1] - a[1] * b[0];
  out[0] = x; out[1] = y; out[2] = z;
  return out;
}
const dot = (a: readonly number[], b: readonly number[]) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/** Which way the front of the pelvis lies from between the hip joints, in the hips' own frame (up, back). */
const BELLY_UP = BELLY[1] + HIP_DROP, BELLY_BACK = BELLY[2], BELLY_WAY = Math.hypot(BELLY_UP, BELLY_BACK);
const G1 = v3(), G2 = v3(), MID = v3();

/**
 * The hips' frame from their points: right, up, forward (the way the person
 * faces), worked back from where the front of the pelvis is.
 */
function pelvisFrame(p: ArrayLike<number>, right: number[], up: number[], forward: number[]): void {
  unit(sub(right, p, J.hipR, J.hipL));
  const [cx, cy, cz] = hips(p, MID);
  G1[0] = p[J.belly * 3] - cx; G1[1] = p[J.belly * 3 + 1] - cy; G1[2] = p[J.belly * 3 + 2] - cz;
  const along = dot(G1, right);
  G1[0] -= right[0] * along; G1[1] -= right[1] * along; G1[2] -= right[2] * along;
  unit(G1);
  cross(G2, right, G1);
  const ty = BELLY_UP / BELLY_WAY, tz = BELLY_BACK / BELLY_WAY;
  for (let k = 0; k < 3; k++) {
    up[k] = G1[k] * ty - G2[k] * tz;
    forward[k] = -(G1[k] * tz + G2[k] * ty);
  }
}

/** The chest's frame: right across the shoulders, up the spine, forward. */
function chestFrame(p: ArrayLike<number>, right: number[], up: number[], forward: number[]): void {
  unit(sub(up, p, J.neck, J.waist));
  sub(right, p, J.shoulderR, J.shoulderL);
  const along = dot(right, up);
  right[0] -= up[0] * along; right[1] -= up[1] * along; right[2] -= up[2] * along;
  unit(right);
  cross(forward, up, right);
}

const D = v3(), K = v3(), S = v3(), T = v3();

/**
 * Which way a knee points, into `K`: the way the kneecap faces. The thigh
 * swings on the hip about the hips' right-to-left axis, so the kneecap faces
 * right × thigh: forward standing, up sitting, down kneeling. Its length is
 * how far the thigh is from lying along that axis; nearly along it (a leg
 * out sideways), the knee points nowhere in particular.
 */
function kneecap(p: ArrayLike<number>, hip: number, knee: number, right: readonly number[]): number {
  unit(sub(D, p, knee, hip));
  cross(K, right, D);
  const k = Math.hypot(K[0], K[1], K[2]);
  if (k > 1e-6) { K[0] /= k; K[1] /= k; K[2] /= k; }
  return k;
}

/**
 * Which way a knee is bent, against the way it can bend: 1 the shin folding
 * straight back behind the thigh, 0 to the side, -1 folding forward, which no
 * knee can. A straight leg is 1.
 */
export function kneeBend(p: ArrayLike<number>, side: 0 | 1): number {
  const s = SIDES[side];
  pelvisFrame(p, R, U, F);
  if (kneecap(p, s.hip, s.knee, R) < 0.3) return 1;
  return bendAgainst(p, s.knee, s.ankle);
}

/**
 * Which way an elbow is bent, against the way it can bend: 1 the forearm
 * folding straight toward the front of the upper arm, -1 straight back,
 * which none can; past -`ELBOW_BACK` it is put right. A straight arm is 1.
 */
export function elbowBend(p: ArrayLike<number>, side: 0 | 1): number {
  const s = SIDES[side];
  chestFrame(p, TR, TU, TF);
  if (kneecap(p, s.shoulder, s.elbow, TR) < 0.3) return 1;
  sub(S, p, s.wrist, s.elbow);
  const along = dot(S, D);
  S[0] -= D[0] * along; S[1] -= D[1] * along; S[2] -= D[2] * along;
  const fold = Math.hypot(S[0], S[1], S[2]);
  return fold < 0.03 ? 1 : dot(S, K) / fold;
}

/** How the shin from `knee` to `ankle` folds against the kneecap `K` and the thigh `D`: see `kneeBend`. Leaves the fold in `S`. */
function bendAgainst(p: ArrayLike<number>, knee: number, ankle: number): number {
  sub(S, p, ankle, knee);
  const along = dot(S, D);
  S[0] -= D[0] * along; S[1] -= D[1] * along; S[2] -= D[2] * along;
  const fold = Math.hypot(S[0], S[1], S[2]);
  return fold < 0.03 ? 1 : -dot(S, K) / fold;
}

/** How much of a limit's overreach each pass takes back: all at once, two limits sharing a point chase each other. */
const GIVE = 0.5;
/**
 * The most a limit moves a point in one pass, metres. Judged from a pose
 * caught half way through a hard landing, a knee can look bent the wrong way
 * by a hand's width when it is not; taken back all at once, the leg is
 * flung round. A real overreach is put right over a few steps.
 */
const MOST = 0.02;

/**
 * Move point `i` by `by` metres along `axis` against the points `from`
 * (the joint it turns on), each giving way by its weight. A limit is the
 * body pushing on itself: moved alone, a knee put right would kick the whole
 * body off the ground.
 */
function nudge(p: Float32Array, i: number, axis: readonly number[], by: number, from: readonly number[]): void {
  let mass = 0;
  for (const k of from) mass += 1 / IW[k];
  const s = (Math.max(-MOST, Math.min(MOST, by)) * GIVE) / (IW[i] + 1 / mass), mine = s * IW[i], theirs = -s / mass;
  p[i * 3] += axis[0] * mine; p[i * 3 + 1] += axis[1] * mine; p[i * 3 + 2] += axis[2] * mine;
  for (const k of from) {
    p[k * 3] += axis[0] * theirs; p[k * 3 + 1] += axis[1] * theirs; p[k * 3 + 2] += axis[2] * theirs;
  }
}

/**
 * Keep point `i` outside a ball at `c` of `radius`, which belongs to points
 * `a` and `b` of the trunk: each gives way by its weight, so an arm trapped
 * under a body lying on it lifts the body rather than going through it.
 */
function outOf(p: Float32Array, i: number, c: readonly number[], radius: number, a: number, b: number): void {
  const dx = p[i * 3] - c[0], dy = p[i * 3 + 1] - c[1], dz = p[i * 3 + 2] - c[2];
  const d = Math.hypot(dx, dy, dz);
  if (d >= radius || d < 1e-6) return;
  const wt = 2 / (IW[a] + IW[b]), wi = IW[i], share = (radius - d) / d / (wi + 1 / wt), back = share / wt;
  p[i * 3] += dx * share * wi; p[i * 3 + 1] += dy * share * wi; p[i * 3 + 2] += dz * share * wi;
  p[a * 3] -= dx * back; p[a * 3 + 1] -= dy * back; p[a * 3 + 2] -= dz * back;
  p[b * 3] -= dx * back; p[b * 3 + 1] -= dy * back; p[b * 3 + 2] -= dz * back;
}

/** How far round from the front of the upper arm an elbow may fold the forearm, as the cosine's negative: about 110°. */
export const ELBOW_BACK = 0.35;
/** Most an upper arm goes back past straight down, as how far back the elbow is. */
const SHOULDER_BACK = Math.sin(1.0) * BODY.upperArm;
const CHEST_BALL = 0.15, BELLY_BALL = 0.13;
const LIMB_POINTS: readonly number[] = [J.elbowL, J.wristL, J.kneeL, J.ankleL, J.toeL, J.elbowR, J.wristR, J.kneeR, J.ankleR, J.toeR];
const CB0 = v3(), CB1 = v3();

/** The joints a distance cannot hold: which way a knee and a foot point, how far back a limb goes, limbs out of the trunk. */
function limits(p: Float32Array): void {
  pelvisFrame(p, R, U, F);
  chestFrame(p, TR, TU, TF);
  for (const s of SIDES) {
    // A knee folding forward: the ankle is in front of the knee, as the
    // kneecap faces. Ankle and knee are put back square across the kneecap,
    // each by its weight, which leaves the leg straight, from where it
    // bends the right way again. An ankle on the ground holds and the knee
    // lifts, as a body on its back would.
    if (kneecap(p, s.hip, s.knee, R) >= 0.3) {
      sub(S, p, s.ankle, s.knee);
      const ahead = dot(S, K);
      if (ahead > 0) nudge(p, s.ankle, K, -ahead, [s.knee]);
    }
    // An elbow folds the forearm toward the front of the upper arm: forward
    // with the arm hanging, up with it raised ahead, behind the head with it
    // raised overhead. The upper arm turns in its socket, so the fold may
    // swing well round to the side, but not back past `ELBOW_BACK`.
    if (kneecap(p, s.shoulder, s.elbow, TR) >= 0.3) {
      sub(S, p, s.wrist, s.elbow);
      const along = dot(S, D);
      S[0] -= D[0] * along; S[1] -= D[1] * along; S[2] -= D[2] * along;
      const fold = dot(S, K), least = -ELBOW_BACK * Math.hypot(S[0], S[1], S[2]);
      if (fold < least) nudge(p, s.wrist, K, least - fold, [s.elbow]);
    }
    // The upper arm no further back than 57°.
    sub(D, p, s.elbow, s.shoulder);
    const armBack = dot(D, TF);
    if (armBack < -SHOULDER_BACK) nudge(p, s.elbow, TF, -SHOULDER_BACK - armBack, [s.shoulder]);
  }
  // Arms and legs kept out of the chest and the belly.
  const w = J.waist * 3;
  CB0[0] = p[w] + TU[0] * 0.25; CB0[1] = p[w + 1] + TU[1] * 0.25; CB0[2] = p[w + 2] + TU[2] * 0.25;
  const [hx, hy, hz] = hips(p, MID);
  CB1[0] = hx + U[0] * 0.05; CB1[1] = hy + U[1] * 0.05; CB1[2] = hz + U[2] * 0.05;
  for (const i of LIMB_POINTS) {
    outOf(p, i, CB0, CHEST_BALL, J.waist, J.neck);
    outOf(p, i, CB1, BELLY_BALL, J.hipL, J.hipR);
  }
}

/**
 * The toes point the way the shin's front does, give or take: across the
 * knee's hinge from the shin, which is the hips' right-to-left axis square
 * to the thigh. Once a step, after the rest, and only for a foot well off
 * the ground: the foot is light, and one turned on the ground against its
 * grip, or against the ground holding it up, would be turned back and forth
 * every step without end. It is turned without setting it moving.
 */
function turnFeet(r: Ragdoll): void {
  const { p, q } = r;
  pelvisFrame(p, R, U, F);
  for (const s of SIDES) {
    if (p[s.toe * 3 + 1] < groundAt(r, s.toe) + RADIUS[s.toe] + 0.05) continue;
    if (kneecap(p, s.hip, s.knee, R) < 0.3) continue;
    const dr = dot(R, D);
    K[0] = R[0] - D[0] * dr; K[1] = R[1] - D[1] * dr; K[2] = R[2] - D[2] * dr;
    unit(sub(S, p, s.ankle, s.knee));
    cross(K, unit(K), S);
    sub(T, p, s.toe, s.ankle);
    const ts = dot(T, S);
    const tx = T[0] - S[0] * ts, ty = T[1] - S[1] * ts, tz = T[2] - S[2] * ts;
    const ahead = tx * K[0] + ty * K[1] + tz * K[2];
    const least = -0.5 * Math.hypot(tx, ty, tz);
    if (ahead >= least) continue;
    const by = (least - ahead) * 0.3;
    for (const b of [p, q]) {
      b[s.toe * 3] += K[0] * by; b[s.toe * 3 + 1] += K[1] * by; b[s.toe * 3 + 2] += K[2] * by;
    }
  }
}

// --- getting up -----------------------------------------------------------------

/**
 * Points part way from `from` to `to` (0..1) into `r`, held still and made
 * a body again: the bones back to length, the joints within their limits,
 * nothing under the ground. For blending a body lying down into a pose.
 */
export function blendToward(r: Ragdoll, from: ArrayLike<number>, to: ArrayLike<number>, t: number, ground: RagdollGround): void {
  const p = r.p;
  for (let i = 0; i < POINTS * 3; i++) p[i] = from[i] + (to[i] - from[i]) * t;
  sampleGround(r, ground);
  IW.set(W);
  // Three times the passes of a step: once a frame, not 240 times a second.
  for (let k = 0; k < ITERATIONS * 3; k++) {
    solve(p);
    onGround(r);
  }
  r.q.set(p);
}

/** Move the whole body by (dx, dz) metres, as it is: for when the scene's origin moves under it. */
export function shiftRagdoll(r: Ragdoll, dx: number, dz: number): void {
  if (dx === 0 && dz === 0) return;
  for (const b of [r.p, r.q, r.mark]) {
    for (let i = 0; i < POINTS; i++) { b[i * 3] += dx; b[i * 3 + 2] += dz; }
  }
  for (let i = 0; i < POINTS; i++) { r.sampledAt[i * 2] += dx; r.sampledAt[i * 2 + 1] += dz; }
}

/** Whether a body lying down lies face down: its chest toward the ground. */
export function faceDown(p: ArrayLike<number>): boolean {
  chestFrame(p, TR, TU, TF);
  return TF[1] < 0;
}
